export const DEFAULT_LOCALE = 'sv-SE';
export const APP_TIME_ZONE = 'Europe/Stockholm';

type DateInput = Date | number | string;

function toDate(value: DateInput): Date {
	return value instanceof Date ? value : new Date(value);
}

export function formatNumber(value: number, locale: string, options?: Intl.NumberFormatOptions): string {
	return new Intl.NumberFormat(locale, options).format(value);
}

export function formatCurrency(
	value: number,
	currency: string,
	locale: string,
	options?: Omit<Intl.NumberFormatOptions, 'currency' | 'style'>,
): string {
	return new Intl.NumberFormat(locale, {
		currency,
		currencyDisplay: 'code',
		style: 'currency',
		...options,
	}).format(value);
}

export function formatPercentage(
	value: number,
	locale: string,
	options?: Omit<Intl.NumberFormatOptions, 'style'>,
): string {
	return new Intl.NumberFormat(locale, {
		style: 'percent',
		...options,
	}).format(value / 100);
}

export function formatDate(
	value: DateInput,
	locale: string,
	options?: Omit<Intl.DateTimeFormatOptions, 'dateStyle' | 'timeStyle'>,
): string {
	const resolvedOptions = options ?? {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	};

	return new Intl.DateTimeFormat(locale, {
		timeZone: APP_TIME_ZONE,
		...resolvedOptions,
	}).format(toDate(value));
}

export function formatTime(
	value: DateInput,
	locale: string,
	options?: Omit<Intl.DateTimeFormatOptions, 'dateStyle' | 'timeStyle'>,
): string {
	const resolvedOptions = options ?? {
		hour: '2-digit',
		hour12: false,
		minute: '2-digit',
	};

	return new Intl.DateTimeFormat(locale, {
		timeZone: APP_TIME_ZONE,
		...resolvedOptions,
	}).format(toDate(value));
}

export function formatDateTime(value: DateInput, locale: string): string {
	return `${formatDate(value, locale)} ${formatTime(value, locale)}`;
}
